import { useQuery } from '@apollo/client';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import {
  Loader,
  Headline,
  ButtonGridView,
  type LabeledValue,
} from '@volumental/common';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenderAgeQuery } from '../../../api/queries/survey-query';
import { useScannerStore } from '../../../store';
import { type GenderStep } from '../../../store/survey';
import { findWithFallback } from './find-value';
import { selectTimeoutMs } from './constants';

interface GenderAgeSelectorProps {
  survey: GenderStep;
  onClose: (gender: LabeledValue<string>) => void;
}

export function GenderAgeSelector({
  survey: { brand, gender },
  onClose,
}: GenderAgeSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const { flowType } = useScannerStore((store) => store);

  const { data, loading } = useQuery(GenderAgeQuery, {
    variables: { brandName: brand.value },
  });
  const [optimisticValue, setOptimisticValue] = useState(gender?.value);
  const translateGenderAge = (genderAge: string): string => {
    if (genderAge === 'male') {
      return t('footwearCategories.male');
    }
    if (genderAge === 'female') {
      return t('footwearCategories.female');
    }
    if (genderAge === 'kids') {
      return t('footwearCategories.kids');
    }
    if (genderAge === 'unisex') {
      return t('footwearCategories.unisex');
    }
    return genderAge;
  };

  const onReady = (selected: string): void => {
    setOptimisticValue(selected);
    if (gender?.value !== selected) {
      usageTracker.track(UsageTrackerEvent.AddGenderToSurvey, {
        genderAge: selected,
        sizing_flow_type: flowType,
      });
    }
    const selectedGenderAge = findWithFallback(selected, genderAges);
    setTimeout(() => {
      onClose(selectedGenderAge);
    }, selectTimeoutMs);
  };

  if (loading || !data?.recommendableGenderAges) {
    return <Loader />;
  }
  const genderAges = data.recommendableGenderAges.map((g) => ({
    label: translateGenderAge(g),
    value: g,
  }));
  return (
    <>
      <Headline style={{ fontSize: '21px' }}>{t('shoeType')}</Headline>
      <ButtonGridView
        options={genderAges}
        onSelect={onReady}
        selected={optimisticValue}
        groupAriaLabel={t('ariaLabels.genderAge')}
        singleColumnMode
      />
    </>
  );
}
